import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AccessRequestsPage() {
  const { getAuthHeaders, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [reqRes, dealsRes] = await Promise.all([
        axios.get(`${API}/access-requests`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals`, { headers: getAuthHeaders() })
      ]);
      setRequests(reqRes.data);
      setDeals(dealsRes.data);
    } catch (error) {
      console.error('Failed to fetch access requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId, status) => {
    try {
      await axios.patch(`${API}/access-requests/${requestId}?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success(`Request ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const getDealName = (dealId) => deals.find(d => d.id === dealId)?.name || dealId;

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
      approved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    };
    return map[status] || map.pending;
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const isAdmin = user?.role === 'admin' || user?.role === 'internal';

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading access requests...</div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="access-requests-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Access Requests</h1>
        <p className="text-muted-foreground">Manage deal access and NDA-gated document requests</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Requests</p><p className="text-2xl font-bold font-mono">{requests.length}</p></div><Shield className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Pending</p><p className="text-2xl font-bold font-mono text-yellow-600">{pendingCount}</p></div><Clock className="h-8 w-8 text-yellow-500" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Approved</p><p className="text-2xl font-bold font-mono text-green-600">{approvedCount}</p></div><CheckCircle className="h-8 w-8 text-green-500" /></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Request Log</CardTitle></CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No access requests yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map(req => (
                  <TableRow key={req.id} data-testid={`request-${req.id}`}>
                    <TableCell className="font-medium text-sm">{getDealName(req.deal_id)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{req.message || '-'}</TableCell>
                    <TableCell><Badge className={getStatusBadge(req.status)}>{req.status}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {req.status === 'pending' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleAction(req.id, 'approved')} data-testid={`approve-${req.id}`}>
                              <CheckCircle size={14} className="mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleAction(req.id, 'rejected')} data-testid={`reject-${req.id}`}>
                              <XCircle size={14} className="mr-1" />Reject
                            </Button>
                          </div>
                        )}
                        {req.status !== 'pending' && <span className="text-xs text-muted-foreground capitalize">{req.status}</span>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
